import { defineStore } from "pinia"
import {
	EventAttendee,
	EventAttendeeStatus,
	PollStatus,
	PollVote,
	Post,
	PostComment,
	PostImageType,
	PostLike,
	PostProductStatus,
	PostStatus,
	PostType,
	ProductKind
} from "~/models/post"
import { ApiResponse } from "~/types/api"
import { IconType } from "~/types/base-icons"

export type PostKind = {
	title: string
	value: string
	icon: IconType
}

export type PostStatusType = {
	name: string
	name2?: string
	name3?: string
	value: PostStatus | PostType | any
	maxLevel?: number
	hasTitle?: boolean
	hasContent?: boolean
	hasLocation?: boolean
	hasInfo?: boolean
	hasFeatures?: boolean
	hasCategory?: boolean
	hasImages?: boolean
	hasCovers?: boolean
	hasProductInfo?: boolean
	borderClass?: string
	bgClass?: string
	txtClass?: string
}

export const usePostStore = defineStore("post", () => {
	const { $i18n } = useNuxtApp()

	const state = {
		articles: ref<Post[]>([]),
		types: ref<PostStatusType[]>([
			{
				name: $i18n.t("article_categories"),
				name2: $i18n.t("articles"),
				name3: $i18n.t("article"),
				value: "article",
				maxLevel: 1,
				hasTitle: true,
				hasContent: true,
				hasLocation: false,
				hasInfo: false,
				hasFeatures: false,
				hasCategory: true,
				hasImages: true,
				hasCovers: true,
				hasProductInfo: false
			}
		]),
		statuses: ref<PostStatusType[]>([
			{
				name: $i18n.t("draft"),
				value: "draft",
				txtClass: "text-gray-500"
			},
			{
				name: $i18n.t("published"),
				value: "publish",
				txtClass: "text-blue-500"
			},
			{
				name: $i18n.t("trash"),
				value: "trash",
				txtClass: "text-red-500"
			}
		]),
		product_statuses: ref<PostStatusType[]>([
			{
				name: $i18n.t("selling"),
				value: "selling",
				txtClass: "text-green-500",
				bgClass: "bg-green-500"
			},
			{
				name: $i18n.t("expired"),
				value: "expired",
				txtClass: "text-slate-500",
				bgClass: "bg-slate-500"
			},
			{
				name: $i18n.t("sold"),
				value: "sold",
				txtClass: "text-red-500",
				bgClass: "bg-red-500"
			}
		]),
		kinds: ref<PostKind[]>([]),
		event_statuses: ref<PostStatusType[]>([
			{
				name: $i18n.t("not_interested"),
				value: "not-interested",
				txtClass: "text-gray-500"
			},
			{
				name: $i18n.t("interested"),
				value: "interested",
				txtClass: "text-blue-500"
			},
			{
				name: $i18n.t("going"),
				value: "going",
				txtClass: "text-green-500"
			}
		]),
		poll_statuses: ref<PostStatusType[]>([
			{
				name: $i18n.t("open"),
				value: "open",
				txtClass: "text-green-500"
			},
			{
				name: $i18n.t("close"),
				value: "close",
				txtClass: "text-red-500"
			}
		])
	}

	const getPost = async (id: number, params?: GetPostRequest) => {
		const data = await $fetch<GetPostResponse>(GetPost.url(id), {
			...useFetchConfig(),
			method: GetPost.method,
			params
		})
		return data.data
	}

	const getTempPost = async (body: GetTempPostRequest) => {
		const data = await $fetch<GetTempPostResponse>(GetTempPost.url(), {
			...useFetchConfig(),
			method: GetTempPost.method,
			body
		})
		return data.data
	}

	const getPostBySlug = async (slug: string, params?: GetPostRequest) => {
		const data = await $fetch<GetPostBySlugResponse>(GetPostBySlug.url(slug), {
			...useFetchConfig(),
			method: GetPostBySlug.method,
			params
		})
		return data.data
	}

	const searchPosts = async (formData: SearchPostsRequest) => {
		const data = await $fetch<SearchPostsResponse>(SearchPosts.url(), {
			...useFetchConfig(),
			method: SearchPosts.method,
			query: formData
		})
		return data.data
	}

	const createPost = async (formData: CreatePostRequest) => {
		const data = await $fetch<CreatePostResponse>(CreatePost.url(), {
			...useFetchConfig(),
			method: CreatePost.method,
			body: formData
		})
		return data.data
	}

	const editPost = async (formData: EditPostRequest) => {
		const data = await $fetch<EditPostResponse>(EditPost.url(), {
			...useFetchConfig(),
			method: EditPost.method,
			body: formData
		})
		return data.data
	}

	const deletePost = async (id: number) => {
		const data = await $fetch<DeletePostResponse>(DeletePost.url(id), {
			...useFetchConfig(),
			method: DeletePost.method
		})
		return data.data
	}

	const addPostImage = async (formData: AddPostImageRequest) => {
		const data = await $fetch<AddPostImageResponse>(AddPostImage.url(), {
			...useFetchConfig(),
			method: AddPostImage.method,
			body: formData
		})
		return data.data
	}

	const removePostImage = async (formData: RemovePostImageRequest) => {
		const data = await $fetch<RemovePostImageResponse>(RemovePostImage.url(), {
			...useFetchConfig(),
			method: RemovePostImage.method,
			body: formData
		})
		return data.data
	}

	const swapPostImage = async (formData: SwapPostImageRequest) => {
		const data = await $fetch<SwapPostImageResponse>(SwapPostImage.url(), {
			...useFetchConfig(),
			method: SwapPostImage.method,
			body: formData
		})
		return data.data
	}

	const addPostLike = async (formData: AddPostLikeRequest) => {
		const data = await $fetch<AddPostLikeResponse>(AddPostLike.url(), {
			...useFetchConfig(),
			method: AddPostLike.method,
			body: formData
		})
		return data.data
	}

	const removePostLike = async (formData: RemovePostLikeRequest) => {
		const data = await $fetch<RemovePostLikeResponse>(RemovePostLike.url(), {
			...useFetchConfig(),
			method: RemovePostLike.method,
			body: formData
		})
		return data.data
	}

	const addPostComment = async (formData: AddPostCommentRequest) => {
		const data = await $fetch<AddPostCommentResponse>(AddPostComment.url(), {
			...useFetchConfig(),
			method: AddPostComment.method,
			body: formData
		})
		return data.data
	}

	const editPostComment = async (formData: EditPostCommentRequest) => {
		const data = await $fetch<EditPostCommentResponse>(EditPostComment.url(), {
			...useFetchConfig(),
			method: EditPostComment.method,
			body: formData
		})
		return data.data
	}

	const searchPostComments = async (query: SearchPostCommentsRequest) => {
		const data = await $fetch<SearchPostCommentsResponse>(SearchPostComments.url(), {
			...useFetchConfig(),
			method: SearchPostComments.method,
			query
		})
		return data.data
	}

	const attendEvent = async (formData: AttendEventRequest) => {
		const data = await $fetch<AttendEventResponse>(AttendEvent.url(), {
			...useFetchConfig(),
			method: AttendEvent.method,
			body: formData
		})
		return data.data
	}

	const searchAttendees = async (query: SearchAttendeesRequest) => {
		const data = await $fetch<SearchAttendeesResponse>(SearchAttendees.url(), {
			...useFetchConfig(),
			method: SearchAttendees.method,
			query
		})
		return data.data
	}

	const votePoll = async (formData: VotePollRequest) => {
		const data = await $fetch<VotePollResponse>(VotePoll.url(), {
			...useFetchConfig(),
			method: VotePoll.method,
			body: formData
		})
		return data.data
	}

	const searchVotes = async (query: SearchVotesRequest) => {
		const data = await $fetch<SearchVotesResponse>(SearchVotes.url(), {
			...useFetchConfig(),
			method: SearchVotes.method,
			query
		})
		return data.data
	}

	return {
		...state,
		getPost,
		getTempPost,
		getPostBySlug,
		searchPosts,
		addPostImage,
		removePostImage,
		swapPostImage,
		createPost,
		editPost,
		deletePost,
		addPostLike,
		removePostLike,
		addPostComment,
		editPostComment,
		searchPostComments,
		attendEvent,
		searchAttendees,
		votePoll,
		searchVotes
	}
})

// post :: create post
export interface CreatePostRequest {
	title?: string
	content?: string
	info?: string
	type?: string
	locale?: string
	status?: string
	slug?: string
	category_id?: number | null
	image_id?: number | null
	published?: string
}
export interface CreatePostResponse extends ApiResponse {
	data: Post
}
const CreatePost = {
	method: "POST" as "POST",
	url: () => "posts/create"
}

// post :: edit post
export interface EditPostRequest {
	id: number
	title?: string
	content?: string
	longitude?: number | null
	latitude?: number | null
	info?: string
	features?: string
	type?: string
	status?: string
	product_status?: string
	rate?: number
	slug: string
	price?: number
	category_id?: number | null
	image_id?: number | null
	event_start?: string
	event_end?: string
	poll_options?: string[]
	poll_status?: PollStatus
	published?: string
	moderation_status?: string
	moderation_reject_reason?: string
}
export interface EditPostResponse extends ApiResponse {
	data: Post
}
const EditPost = {
	method: "POST" as "POST",
	url: () => "posts/edit"
}

// post :: delete post
export interface DeletePostRequest {}
export interface DeletePostResponse extends ApiResponse {
	data: null
}
const DeletePost = {
	method: "POST" as "POST",
	url: (id: any) => `posts/delete/${id}`
}

// post :: get post
export interface GetPostRequest {
	moderation_statuses?: string
	user_moderation_statuses?: string
	business_moderation_statuses?: string
	is_moderated?: boolean
}
export interface GetPostResponse extends ApiResponse {
	data: Post
}
const GetPost = {
	method: "GET" as "GET",
	url: (id: any) => `posts/${id}`
}

// post :: get temp post
export interface GetTempPostRequest {
	type: PostType
	business_id?: number | null
}
export interface GetTempPostResponse extends ApiResponse {
	data: Post
}
const GetTempPost = {
	method: "POST" as "POST",
	url: () => `posts/temp`
}

// post :: get post by slug
export interface GetPostBySlugRequest {
	moderation_statuses?: string
	user_moderation_statuses?: string
	business_moderation_statuses?: string
	is_moderated?: boolean
}
export interface GetPostBySlugResponse extends ApiResponse {
	data: Post
}
const GetPostBySlug = {
	method: "GET" as "GET",
	url: (slug: any) => `posts/slug/${slug}`
}

// post :: search posts
export interface SearchPostsRequest {
	search?: string
	page?: number
	limit?: number
	status?: PostStatus
	type?: PostType
	types?: string
	is_free?: boolean
	locale?: string
	kind?: ProductKind
	category_id?: number | null
	category_slug?: string | null
	user_id?: number | null
	except_user_id?: number | null
	business_id?: number | null
	neighbourhood?: string | null
	product_status?: PostProductStatus | null
	is_liked?: boolean
	is_attendee?: boolean
	event_status?: EventAttendeeStatus
	order_by?: string
	direction?: string
	is_event_active?: boolean
	moderation_statuses?: string
	user_moderation_statuses?: string
	business_moderation_statuses?: string
	is_moderated?: boolean
	is_blocked?: boolean
}
export interface SearchPostsResponse extends ApiResponse {
	data: {
		posts: Post[]
		total: number
	}
}
const SearchPosts = {
	method: "GET" as "GET",
	url: () => `posts/search`
}

// post :: add post image
export interface AddPostImageRequest {
	id: number
	image_id: number
	type: PostImageType
}
export interface AddPostImageResponse extends ApiResponse {
	data: Post
}
const AddPostImage = {
	method: "POST" as "POST",
	url: () => `posts/add-image`
}

// post :: remove post image
export interface RemovePostImageRequest {
	id: number
	image_id: number
	type: PostImageType
}
export interface RemovePostImageResponse extends ApiResponse {
	data: Post
}
const RemovePostImage = {
	method: "POST" as "POST",
	url: () => `posts/remove-image`
}

// post :: swap post image
export interface SwapPostImageRequest {
	id: number
	image_1_id: number
	image_2_id: number
	type: PostImageType
}
export interface SwapPostImageResponse extends ApiResponse {
	data: Post
}
const SwapPostImage = {
	method: "POST" as "POST",
	url: () => `posts/swap-image`
}

// post :: add post like
export interface AddPostLikeRequest {
	post_id: number
}
export interface AddPostLikeResponse extends ApiResponse {
	data: PostLike
}
const AddPostLike = {
	method: "POST" as "POST",
	url: () => `posts/add-like`
}

// post :: remove post like
export interface RemovePostLikeRequest {
	post_id: number
}
export interface RemovePostLikeResponse extends ApiResponse {
	data: null
}
const RemovePostLike = {
	method: "POST" as "POST",
	url: () => `posts/remove-like`
}

// post :: add post comment
export interface AddPostCommentRequest {
	post_id: number
	message: string
}
export interface AddPostCommentResponse extends ApiResponse {
	data: PostComment
}
const AddPostComment = {
	method: "POST" as "POST",
	url: () => `posts/add-comment`
}

// post :: edit post comment
export interface EditPostCommentRequest {
	comment_id: number
	moderation_status?: string
	moderation_reject_reason?: string
}
export interface EditPostCommentResponse extends ApiResponse {
	data: PostComment
}
const EditPostComment = {
	method: "POST" as "POST",
	url: () => `posts/edit-comment`
}

// post :: search post comments
export interface SearchPostCommentsRequest {
	post_id: number
	page?: number
	limit?: number
	order_by?: "updated" | "created"
	direction: "asc" | "desc"
	moderation_statuses?: string
	user_moderation_statuses?: string
	is_moderated?: boolean
	is_blocked?: boolean
}
export interface SearchPostCommentsResponse extends ApiResponse {
	data: {
		comments: PostComment[]
		total: number
	}
}
const SearchPostComments = {
	method: "GET" as "GET",
	url: () => `posts/search-comments`
}

// post :: attend event
export interface AttendEventRequest {
	post_id: number
	status: EventAttendeeStatus
}
export interface AttendEventResponse extends ApiResponse {
	data: EventAttendee | null
}
const AttendEvent = {
	method: "POST" as "POST",
	url: () => `posts/attend-event`
}

// post :: search attendees
export interface SearchAttendeesRequest {
	post_id: number
	page?: number
	limit?: number
	order_by?: "updated" | "created"
	direction?: "asc" | "desc"
	status?: EventAttendeeStatus
}
export interface SearchAttendeesResponse extends ApiResponse {
	data: {
		attendees: EventAttendee[]
		total: number
	}
}
const SearchAttendees = {
	method: "GET" as "GET",
	url: () => `posts/search-attendees`
}

// post :: vote poll
export interface VotePollRequest {
	post_id: number
	option_id: number
}
export interface VotePollResponse extends ApiResponse {
	data: Post
}
const VotePoll = {
	method: "POST" as "POST",
	url: () => `posts/vote-poll`
}

// post :: search votes
export interface SearchVotesRequest {
	post_id: number
	option_id?: number
	page?: number
	limit?: number
	order_by?: "updated" | "created"
	direction?: "asc" | "desc"
}
export interface SearchVotesResponse extends ApiResponse {
	data: {
		votes: PollVote[]
		total: number
	}
}
const SearchVotes = {
	method: "GET" as "GET",
	url: () => `posts/search-votes`
}

export const usePostApiUrl = () => ({
	CreatePost,
	EditPost,
	DeletePost,
	GetPost,
	GetPostBySlug,
	SearchPosts,
	AddPostImage,
	RemovePostImage,
	SwapPostImage,
	AddPostLike,
	RemovePostLike,
	AddPostComment,
	EditPostComment,
	SearchPostComments,
	VotePoll,
	SearchVotes
})
